import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { StorageConfig } from '../../types/storage-config.types';
import { FIREBASE_CACHE_THRESHOLD } from '../../types/file.types';
import {
  IStorageAdapter,
  StorageFile,
  UploadOptions,
} from './storage-adapter.interface';
import { firebaseStorageAdapter } from './firebase-adapter';
import { nasStorageAdapter } from './nas-adapter';
import imageCompression from 'browser-image-compression';
import chunkedUploadService from '../chunked-upload.service';

/**
 * 통합 스토리지 서비스
 * 
 * Firestore의 설정에 따라 Firebase Storage 또는 NAS를 자동으로 선택
 * - firebase: Firebase Storage 단독 사용
 * - hybrid: 파일 크기/타입에 따라 자동 전환
 */
class UnifiedStorageService {
  private config: StorageConfig | null = null;
  private configLoaded: boolean = false;

  /**
   * Firestore에서 스토리지 설정 로드
   */
  private async loadConfig(): Promise<StorageConfig> {
    if (this.config && this.configLoaded) {
      return this.config;
    }

    try {
      const configDoc = await getDoc(doc(db, 'system', 'storageConfig'));
      
      if (configDoc.exists()) {
        this.config = configDoc.data() as StorageConfig;
        this.configLoaded = true;
      } else {
        // 기본값: Firebase 단독
        this.config = {
          mode: 'firebase-only',
          updatedAt: new Date().toISOString(),
        };
        this.configLoaded = true;
      }

      // NAS 프록시 URL은 환경변수(REACT_APP_NAS_PROXY_URL)에서 자동 로드됨
      // nas-adapter.ts에서 process.env.REACT_APP_NAS_PROXY_URL 사용
      
      return this.config;
    } catch (error) {
      console.error('스토리지 설정 로드 실패:', error);
      // 에러 시 기본값 반환
      this.config = {
        mode: 'firebase-only',
        updatedAt: new Date().toISOString(),
      };
      return this.config;
    }
  }

  /**
   * 설정 강제 리로드
   */
  async reloadConfig(): Promise<void> {
    this.configLoaded = false;
    await this.loadConfig();
  }

  /**
   * Firebase 캐시 여부 판단
   * 10MB 이하 파일은 Firebase에도 캐시
   */
  private shouldCacheInFirebase(file: File): boolean {
    return file.size <= FIREBASE_CACHE_THRESHOLD;
  }

  /**
   * 파일에 적합한 어댑터 선택
   * @param file 업로드할 파일
   * @param path 저장 경로
   */
  private async selectAdapter(file: File, path: string): Promise<IStorageAdapter> {
    const config = await this.loadConfig();

    // Firebase 단독 모드
    if (config.mode === 'firebase-only') {
      return firebaseStorageAdapter;
    }

    // 하이브리드 모드
    if (config.mode === 'hybrid' && config.storageRules) {
      const rules = config.storageRules;

      // 파일 크기 체크
      if (rules.largeSizeThreshold) {
        const thresholdBytes = rules.largeSizeThreshold * 1024 * 1024; // MB to Bytes
        if (file.size >= thresholdBytes) {
          return nasStorageAdapter; // 큰 파일은 NAS로
        }
      }

      // 파일 타입별 규칙
      if (rules.fileTypeRules) {
        const fileExtension = path.split('.').pop()?.toLowerCase();
        
        // 3D 파일
        if (fileExtension && ['stl', 'step', 'stp', 'obj', 'fbx'].includes(fileExtension)) {
          return rules.fileTypeRules.models === 'nas' 
            ? nasStorageAdapter 
            : firebaseStorageAdapter;
        }

        // 이미지 파일
        if (fileExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
          return rules.fileTypeRules.images === 'nas' 
            ? nasStorageAdapter 
            : firebaseStorageAdapter;
        }

        // 문서 파일
        if (fileExtension && ['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExtension)) {
          return rules.fileTypeRules.documents === 'nas' 
            ? nasStorageAdapter 
            : firebaseStorageAdapter;
        }
      }
    }

    // 기본값: Firebase
    return firebaseStorageAdapter;
  }

  /**
   * 파일 업로드 (공통)
   * 
   * 파일 크기에 따른 자동 분기:
   * - 30MB 이상: ChunkedUpload (Cloud Run)
   * - 10-30MB: NAS Proxy
   * - 10MB 이하: Firebase Storage (또는 Firebase 캐시 + NAS)
   * 
   * 하이브리드 모드:
   * 1. NAS에 먼저 업로드 (마스터)
   * 2. 10MB 이하면 Firebase에도 캐시
   * 3. NAS URL 반환
   */
  async uploadFile(filePath: string, file: File): Promise<string> {
    const config = await this.loadConfig();
    const fileSizeMB = file.size / (1024 * 1024);

    // 🔥 30MB 이상: ChunkedUpload 사용 (하이브리드 모드만)
    if (config.mode === 'hybrid' && fileSizeMB >= 30) {
      console.log(`🔥 대용량 파일 (${fileSizeMB.toFixed(2)}MB): ChunkedUpload 사용`);
      
      // ChunkedUpload로 NAS에 업로드
      const nasPath = await chunkedUploadService.uploadLargeFile(
        file,
        filePath,
        (progress) => {
          console.log(`📊 업로드 진행률: ${progress.percentage}% (${progress.message})`);
        }
      );

      // NAS Proxy URL 생성
      const proxyUrl = process.env.REACT_APP_NAS_PROXY_URL || '';
      const downloadUrl = `${proxyUrl}/api/download?path=${encodeURIComponent(nasPath)}`;
      
      console.log(`✅ ChunkedUpload 완료: ${downloadUrl}`);
      return downloadUrl;
    }

    // Firebase 단독 모드
    if (config.mode === 'firebase-only') {
      const result = await firebaseStorageAdapter.upload(file, filePath);
      return result.url;
    }

    // 하이브리드 모드: 듀얼 스토리지 (32MB 미만)
    if (config.mode === 'hybrid') {
      console.log(`🚀 하이브리드 모드: NAS 업로드 시작 - ${filePath}`);
      
      // 1. NAS에 먼저 업로드 (마스터)
      const nasResult = await nasStorageAdapter.upload(file, filePath);
      console.log(`✅ NAS 업로드 완료:`, nasResult);

      // 2. 10MB 이하면 Firebase에도 캐시
      if (this.shouldCacheInFirebase(file)) {
        try {
          await firebaseStorageAdapter.upload(file, filePath);
          console.log(`✅ Firebase 캐시 생성: ${filePath} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        } catch (error) {
          console.warn('Firebase 캐시 생성 실패 (무시):', error);
        }
      }

      // 3. NAS URL 반환 (마스터)
      console.log(`📤 반환할 URL:`, nasResult.url);
      return nasResult.url;
    }

    // 기본값: Firebase
    const result = await firebaseStorageAdapter.upload(file, filePath);
    return result.url;
  }

  /**
   * Public 파일 URL 가져오기
   * 
   * 하이브리드 모드:
   * 1. Firebase 캐시 먼저 확인
   * 2. 없으면 NAS URL 반환
   */
  async getPublicFileUrl(filePath: string): Promise<string> {
    const config = await this.loadConfig();
    
    // Firebase 단독 모드
    if (config.mode === 'firebase-only') {
      return await firebaseStorageAdapter.getDownloadURL(filePath);
    }

    // 하이브리드 모드: Firebase 캐시 우선
    if (config.mode === 'hybrid') {
      try {
        // 1. Firebase 캐시 확인 시도
        const firebaseUrl = await firebaseStorageAdapter.getDownloadURL(filePath);
        console.log(`✅ Firebase 캐시 사용: ${filePath}`);
        return firebaseUrl;
      } catch (error) {
        // 2. 캐시 없음 → NAS URL 반환
        console.log(`📦 NAS에서 다운로드: ${filePath}`);
        return await nasStorageAdapter.getDownloadURL(filePath);
      }
    }
    
    // 기본: Firebase
    return await firebaseStorageAdapter.getDownloadURL(filePath);
  }

  /**
   * 이미지 업로드 (금형용)
   */
  async uploadImage(file: File, moldId: string, index: number): Promise<string> {
    const fileName = `${Date.now()}_${index}_${file.name}`;
    const filePath = `molds/${moldId}/images/${fileName}`;
    return await this.uploadFile(filePath, file);
  }

  /**
   * 이미지 + 썸네일 업로드
   */
  async uploadImageWithThumbnail(
    file: File,
    moldId: string,
    index: number
  ): Promise<{ original: string; thumbnail: string }> {
    const timestamp = Date.now();
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const ext = file.name.split('.').pop() || 'jpg';

    // 어댑터 선택
    const adapter = await this.selectAdapter(file, `molds/${moldId}/images/`);

    // 원본 업로드
    const originalFileName = `${timestamp}_${index}_${file.name}`;
    const originalPath = `molds/${moldId}/images/${originalFileName}`;
    const originalResult = await adapter.upload(file, originalPath);

    // 썸네일 생성
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 500,
      useWebWorker: true,
    });

    // 썸네일 업로드
    const thumbnailFileName = `${timestamp}_${index}_thumb_${baseName}.${ext}`;
    const thumbnailPath = `molds/${moldId}/thumbnails/${thumbnailFileName}`;
    const thumbnailResult = await adapter.upload(thumbnailFile, thumbnailPath);

    return {
      original: originalResult.url,
      thumbnail: thumbnailResult.url,
    };
  }

  /**
   * 3D 파일 업로드
   */
  async upload3DFile(
    file: File,
    moldId: string
  ): Promise<{ url: string; name: string; size: number }> {
    const fileName = `${Date.now()}_${file.name}`;
    const filePath = `molds/${moldId}/3d/${fileName}`;
    const url = await this.uploadFile(filePath, file);

    return {
      url,
      name: file.name,
      size: file.size,
    };
  }

  /**
   * Customer 로고 업로드
   */
  async uploadCustomerLogo(
    file: File,
    customerId: string
  ): Promise<{ logo: string; logoThumbnail: string }> {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';

    const adapter = await this.selectAdapter(file, `customers/${customerId}/`);

    // 원본 업로드
    const originalFileName = `${timestamp}_logo.${ext}`;
    const originalPath = `customers/${customerId}/${originalFileName}`;
    const logoResult = await adapter.upload(file, originalPath);

    // 썸네일 생성 (200x200)
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 200,
      useWebWorker: true,
    });

    const thumbnailFileName = `${timestamp}_logo_thumb.${ext}`;
    const thumbnailPath = `customers/${customerId}/${thumbnailFileName}`;
    const thumbnailResult = await adapter.upload(thumbnailFile, thumbnailPath);

    return {
      logo: logoResult.url,
      logoThumbnail: thumbnailResult.url,
    };
  }

  /**
   * Location 이미지 업로드
   */
  async uploadLocationImage(
    file: File,
    locationId: string,
    index: number
  ): Promise<{ original: string; thumbnail: string }> {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';

    const adapter = await this.selectAdapter(file, `locations/${locationId}/`);

    // 원본 업로드
    const originalFileName = `${timestamp}_${index}.${ext}`;
    const originalPath = `locations/${locationId}/images/${originalFileName}`;
    const originalResult = await adapter.upload(file, originalPath);

    // 썸네일 생성 (500x500)
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 500,
      useWebWorker: true,
    });

    const thumbnailFileName = `${timestamp}_${index}_thumb.${ext}`;
    const thumbnailPath = `locations/${locationId}/thumbnails/${thumbnailFileName}`;
    const thumbnailResult = await adapter.upload(thumbnailFile, thumbnailPath);

    return {
      original: originalResult.url,
      thumbnail: thumbnailResult.url,
    };
  }

  /**
   * 파일 삭제 (단일)
   */
  async deleteFile(filePath: string): Promise<void> {
    const config = await this.loadConfig();

    // 경로에서 어댑터 판단
    if (config.mode === 'hybrid' && filePath.includes('nas/')) {
      await nasStorageAdapter.delete(filePath);
    } else {
      await firebaseStorageAdapter.delete(filePath);
    }
  }

  /**
   * 여러 파일 삭제 (URL 기반)
   * 기존 storage.service.ts와 호환
   */
  async deleteFiles(fileUrls: string[]): Promise<void> {
    const config = await this.loadConfig();

    for (const url of fileUrls) {
      try {
        // NAS URL 감지 (프록시 서버 URL 포함)
        if (config.mode === 'hybrid' && 
            config.nasConnection?.proxyUrl && 
            url.includes(config.nasConnection.proxyUrl)) {
          // NAS 파일: URL에서 경로 추출 필요
          const pathMatch = url.match(/\/api\/download\/(.+)$/);
          if (pathMatch) {
            const path = decodeURIComponent(pathMatch[1]);
            await nasStorageAdapter.delete(path);
          }
        } else {
          // Firebase 파일: 기존 로직 사용
          const path = firebaseStorageAdapter.extractPathFromUrl(url);
          if (path) {
            await firebaseStorageAdapter.delete(path);
          }
        }
      } catch (error) {
        console.error('파일 삭제 실패:', url, error);
      }
    }
  }

  /**
   * NAS 연결 테스트 (관리자용)
   */
  async testNASConnection(): Promise<{ success: boolean; message: string }> {
    return await nasStorageAdapter.testConnection();
  }

  /**
   * 현재 스토리지 설정 조회
   */
  async getCurrentConfig(): Promise<StorageConfig | null> {
    return await this.loadConfig();
  }

  /**
   * 텍스트 파일 업로드
   * 문자열을 파일로 변환하여 업로드 (info 파일 생성용)
   * 
   * @param filePath 저장 경로 (예: "egmms_files/projects/P-2024-001/_project_info.txt")
   * @param content 텍스트 내용
   * @param filename 파일명 (기본값: "_info.txt")
   */
  async uploadTextFile(
    filePath: string,
    content: string,
    filename: string = '_info.txt'
  ): Promise<string> {
    // 텍스트를 Blob으로 변환
    const blob = new Blob([content], { type: 'text/plain; charset=utf-8' });
    
    // Blob을 File 객체로 변환
    const file = new File([blob], filename, { type: 'text/plain' });
    
    // 일반 파일 업로드 로직 사용
    return await this.uploadFile(filePath, file);
  }

  /**
   * 폴더 구조 생성 (NAS 전용)
   * Firebase Storage는 폴더 개념이 없으므로 NAS만 처리
   * 
   * @param folderPaths 폴더 경로 배열 (예: ["egmms_files/projects/P-2024-001", "egmms_files/projects/P-2024-001/drawings", ...])
   */
  async createFolderStructure(folderPaths: string[]): Promise<void> {
    const config = await this.loadConfig();

    // Firebase 단독 모드는 폴더 생성 불필요 (파일 업로드 시 자동 생성)
    if (config.mode === 'firebase-only') {
      console.log('✅ Firebase 모드: 폴더 생성 불필요 (자동 생성)');
      return;
    }

    // 하이브리드 모드: NAS 폴더 생성
    if (config.mode === 'hybrid') {
      try {
        await nasStorageAdapter.createDirectories(folderPaths);
        console.log('✅ NAS 폴더 구조 생성 완료:', folderPaths.length, '개');
      } catch (error) {
        console.error('⚠️ NAS 폴더 구조 생성 실패:', error);
        // 폴더 생성 실패해도 계속 진행 (파일 업로드 시 자동 생성될 수 있음)
      }
    }
  }
}

// 싱글톤 인스턴스 생성 및 export
const unifiedStorageService = new UnifiedStorageService();
export default unifiedStorageService;
